<template>
  <div :data-testid="toKebabCase(`${customField.type}-${customField.label}`)">
    <field-value v-bind="{ ...$attrs }" :label="customField.label">
      <template #value>
        <template v-if="!isNull(computedValue) && typeof computedValue !== 'undefined'">
          <template v-if="customField.type === CustomFieldType.MultiDocument">
            <v-link v-for="(link, index) in computedValue" :key="`${link}-${index}`" :href="link">
              {{ truncateString(getDocumentNameFromUrl(link), 20) }}
            </v-link>
          </template>
          <m-link v-else-if="customField.type === CustomFieldType.Document" :href="computedValue">
            {{ truncateString(getDocumentNameFromUrl(computedValue), 20) }}
          </m-link>
          <m-group
            v-else-if="customField.type === CustomFieldType.DropDownMultiSelect"
            gap="spacing-2">
            <m-tag v-for="(val, index) in computedValue" :key="`${val}-${index}`">
              {{ val }}
            </m-tag>
          </m-group>

          <m-text
            v-else
            variant="body-md"
            :data-testid="toKebabCase(`${customField.type}-${customField.label}-value`)">
            {{ computedValue }}
          </m-text>
        </template>

        <no-content v-else></no-content>
      </template>
    </field-value>
  </div>
</template>
<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { FieldValue, NoContent, VLink } from '@/components';
import {
  toKebabCase,
  CustomFieldType,
  getDocumentNameFromUrl,
  truncateString,
  getCustomFieldFormattedValue,
  formatDateTimeWithMilitarySupport,
  LuxonDateTimeFormats
} from '@nova/core';
import { isNull } from 'lodash';
import { useCompanyDisplayFormat } from '@/modules/company/composables/useCompanyDisplayFormat';
import { isCompanyDisplayFormatEnabled } from '@/modules/company/companyDisplayFormatFlag';
// TODO: Cleanup duplicate code and document this
/**
 * Custom Field Value - used to display a custom field key/value in a stacked format
 * @displayName Custom Field Value
 */
export default {
  name: 'CustomFieldValue',
  components: { NoContent, FieldValue, VLink },
  props: {
    /**
     * shape of ICustomField
     */
    customField: {
      type: Object,
      required: true
    },
    /**
     * Warehouse of Appointment where the custom field is on
     */
    warehouse: {
      type: Object,
      required: true
    },
    /**
     * Optional company override for date/time display (e.g. public appointment user.company).
     * Falls back to Auth/me.company when omitted.
     */
    company: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    const store = useStore();
    return {
      companyDisplayFormat: useCompanyDisplayFormat(
        computed(() => props.company ?? store.getters['Auth/me']?.company ?? null)
      )
    };
  },
  computed: {
    warehouseMilitaryTimeEnabled() {
      return this.$isMilitaryTimeEnabled(this.warehouse);
    },
    computedValue() {
      let computedValue = this.customField.value;
      if (this.customField.type === CustomFieldType.Timestamp) {
        if (!computedValue) {
          return null;
        }
        if (!isCompanyDisplayFormatEnabled()) {
          return formatDateTimeWithMilitarySupport(
            computedValue,
            this.warehouse.timezone,
            LuxonDateTimeFormats.DateSlashedTimestamp12HourTimeAMPM,
            this.warehouseMilitaryTimeEnabled,
            LuxonDateTimeFormats.DateSlashedTimestamp24HourTime
          );
        }
        return getCustomFieldFormattedValue(this.customField, {
          [CustomFieldType.Timestamp]: this.companyDisplayFormat.companyTimestampFieldFormatOptions(
            this.warehouse.timezone
          )
        });
      } else if (this.customField.type === CustomFieldType.Date) {
        if (!computedValue) {
          return null;
        }
        if (!isCompanyDisplayFormatEnabled()) {
          return formatDateTimeWithMilitarySupport(
            computedValue,
            this.warehouse.timezone,
            LuxonDateTimeFormats.DateDashed,
            this.warehouseMilitaryTimeEnabled,
            LuxonDateTimeFormats.DateDashed
          );
        }
        return this.companyDisplayFormat.formatCompanyDate(computedValue, this.warehouse.timezone);
      } else if (this.customField.type === CustomFieldType.Bool) {
        computedValue = computedValue ? 'Yes' : 'No';
      }
      return computedValue;
    }
  },
  data() {
    return {
      CustomFieldType
    };
  },
  methods: {
    isNull,
    toKebabCase,
    getDocumentNameFromUrl,
    truncateString
  }
};
</script>
